import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from './ProductCard';
import { products } from '../data/products';

const checkboxClasses = "ml-2 w-4 h-4 accent-[#F2B705] bg-gray-100 border-gray-300 rounded focus:ring-[#F2B705]";

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    brand: [],
    category: [],
    rating: 0,
    minPrice: '',
    maxPrice: '',
    showDiscountedOnly: false
  });

  const [availableBrands, setAvailableBrands] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    const normalizedQuery = query.toLowerCase().trim();

    const results = products.filter(product => {
      const name = product.name?.toLowerCase() || '';
      const description = product.description?.toLowerCase() || '';
      const brand = product.brand?.toLowerCase() || '';
      const keywords = (product.keywords || []).join(' ').toLowerCase();

      // البحث بيدور دلوقتي في الاسم والوصف والبراند والكلمات المفتاحية مع بعض
      // عشان تشتغل مع بحث عربي أو إنجليزي (زي "iPhone" أو "آيفون")
      return (
        name.includes(normalizedQuery) ||
        description.includes(normalizedQuery) ||
        brand.includes(normalizedQuery) ||
        keywords.includes(normalizedQuery)
      );
    });

    const brandsInResults = [...new Set(results.map(p => p.brand).filter(Boolean))];
    const categoriesInResults = [...new Set(results.map(p => p.category).filter(Boolean))];
    setAvailableBrands(brandsInResults);
    setAvailableCategories(categoriesInResults);

    const filteredByAll = results.filter(p => {
      const brandMatch = activeFilters.brand.length === 0 || (p.brand && activeFilters.brand.includes(p.brand));
      const categoryMatch = activeFilters.category.length === 0 || (p.category && activeFilters.category.includes(p.category));
      const ratingMatch = activeFilters.rating === 0 || p.rating >= activeFilters.rating;
      const minPrice = parseFloat(activeFilters.minPrice) || 0;
      const maxPrice = parseFloat(activeFilters.maxPrice) || Infinity;
      const priceMatch = p.price >= minPrice && p.price <= maxPrice;
      const discountMatch = !activeFilters.showDiscountedOnly || (activeFilters.showDiscountedOnly && p.discount);

      return brandMatch && categoryMatch && ratingMatch && priceMatch && discountMatch;
    });

    setFilteredProducts(filteredByAll);
  }, [query, activeFilters]);

  const handleCheckboxFilterChange = (filterType, value) => {
    setActiveFilters(prev => {
      const currentFilters = prev[filterType];
      if (currentFilters.includes(value)) {
        return { ...prev, [filterType]: currentFilters.filter(item => item !== value) };
      } else {
        return { ...prev, [filterType]: [...currentFilters, value] };
      }
    });
  };

  const handleRatingFilterChange = (value) => {
    setActiveFilters(prev => ({
      ...prev,
      rating: prev.rating === value ? 0 : value,
    }));
  };

  const handlePriceChange = (e, type) => {
    setActiveFilters(prev => ({
      ...prev,
      [type]: e.target.value,
    }));
  };

  const handleDiscountToggle = (e) => {
    setActiveFilters(prev => ({
      ...prev,
      showDiscountedOnly: e.target.checked,
    }));
  };

  const RatingStars = ({ rating, count }) => (
    <div className="flex items-center text-sm">
      <span className="text-[#F2B705]">{'★'.repeat(rating)}</span>
      <span className="text-gray-300">{'★'.repeat(5 - rating)}</span>
      <span className="text-gray-600 mr-2">و أكثر</span>
      {count && <span className="text-gray-500 text-xs">({count})</span>}
    </div>
  );

  return (
    <div className="container mx-auto p-8 md:p-16" dir="rtl">
      <div className="text-center mb-10">
        <span
          className="inline-block px-4 py-1 rounded-full bg-[#F2B705]/15 text-[#1B1B1F] text-sm font-bold -rotate-2 mb-3"
          style={{ fontFamily: "'Baloo Bhaijaan 2', sans-serif" }}
        >
          #نتائج_البحث
        </span>
        <h1
          className="text-3xl md:text-4xl font-extrabold text-[#1B1B1F]"
          style={{ fontFamily: "'Baloo Bhaijaan 2', sans-serif" }}
        >
          نتائج البحث عن "{query}"
        </h1>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Sidebar for Filters */}
        <div className="lg:col-span-1 bg-white p-6 rounded-2xl shadow-[0_2px_10px_rgba(27,27,31,0.06)] h-fit border border-black/5">
          <h2
            className="text-xl font-bold mb-4 border-b border-black/10 pb-3 text-[#1B1B1F]"
            style={{ fontFamily: "'Baloo Bhaijaan 2', sans-serif" }}
          >
            الفلاتر
          </h2>

          {/* Price Filter */}
          <div className="mb-6">
            <h3 className="font-semibold text-[#1B1B1F] mb-2 text-sm">السعر (ر.س)</h3>
            <div className="flex gap-2 mb-2">
              <input
                type="number"
                placeholder="الحد الأدنى"
                value={activeFilters.minPrice}
                onChange={(e) => handlePriceChange(e, 'minPrice')}
                className="w-1/2 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F2B705] text-right text-sm"
              />
              <input
                type="number"
                placeholder="الحد الأقصى"
                value={activeFilters.maxPrice}
                onChange={(e) => handlePriceChange(e, 'maxPrice')}
                className="w-1/2 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F2B705] text-right text-sm"
              />
            </div>
          </div>

          {/* Rating Filter */}
          <div className="mb-6 border-t border-black/10 pt-4">
            <h3 className="font-semibold text-[#1B1B1F] mb-2 text-sm">التقييم</h3>
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center mb-2 cursor-pointer" onClick={() => handleRatingFilterChange(star)}>
                <input
                  type="radio"
                  id={`rating-${star}`}
                  checked={activeFilters.rating === star}
                  readOnly
                  className={checkboxClasses}
                />
                <label htmlFor={`rating-${star}`} className="text-gray-700 cursor-pointer">
                  <RatingStars rating={star} />
                </label>
              </div>
            ))}
          </div>

          {/* Category Filter */}
          {availableCategories.length > 0 && (
            <div className="mb-6 border-t border-black/10 pt-4">
              <h3 className="font-semibold text-[#1B1B1F] mb-2 text-sm">الفئة</h3>
              {availableCategories.map(category => (
                <div key={category} className="flex items-center mb-1.5">
                  <input
                    type="checkbox"
                    id={`category-${category}`}
                    checked={activeFilters.category.includes(category)}
                    onChange={() => handleCheckboxFilterChange('category', category)}
                    className={checkboxClasses}
                  />
                  <label htmlFor={`category-${category}`} className="text-gray-700 text-sm">{category}</label>
                </div>
              ))}
            </div>
          )}

          {/* Discounted Products Filter */}
          <div className="mb-6 border-t border-black/10 pt-4">
            <h3 className="font-semibold text-[#1B1B1F] mb-2 text-sm">عروض خاصة</h3>
            <div className="flex items-center mb-1">
              <input
                type="checkbox"
                id="showDiscounted"
                checked={activeFilters.showDiscountedOnly}
                onChange={handleDiscountToggle}
                className={checkboxClasses}
              />
              <label htmlFor="showDiscounted" className="text-gray-700 text-sm">عرض المنتجات المخفضة فقط</label>
            </div>
          </div>

          {/* Brand Filter */}
          {availableBrands.length > 0 && (
            <div className="mb-2 border-t border-black/10 pt-4">
              <h3 className="font-semibold text-[#1B1B1F] mb-2 text-sm">الماركة</h3>
              {availableBrands.map(brand => (
                <div key={brand} className="flex items-center mb-1.5">
                  <input
                    type="checkbox"
                    id={`brand-${brand}`}
                    checked={activeFilters.brand.includes(brand)}
                    onChange={() => handleCheckboxFilterChange('brand', brand)}
                    className={checkboxClasses}
                  />
                  <label htmlFor={`brand-${brand}`} className="text-gray-700 text-sm">{brand}</label>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main content for products */}
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => <ProductCard key={product.id} product={product} />)
            ) : (
              <p className="col-span-full text-center text-gray-500 py-12">لا توجد نتائج مطابقة.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResultsPage;
